import React, { useEffect, useState } from "react";
import { Popover, List, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Notification from "./Notification";
import { useUser } from "../UserContext";

// Define the props type
interface NotificationsProps {
  open: boolean;
  handleClose: () => void;
}

// Define the Notification interface
interface NotificationData {
  id: number;
  user_id: number;
  notification_type: string;
  message: string;
  status: "unread" | "read";
  created_at: string;
}

const Notifications: React.FC<NotificationsProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!currentUser?.user_id) {
        setNotifications([]);
        return;
      }

      try {
        const response = await fetch(
          `${
            import.meta.env.VITE_API_URL
          }/notifications/notifications?user_id=${currentUser.user_id}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setError("Failed to load notifications");
        setNotifications([]);
      }
    };

    fetchNotifications();
  }, [currentUser?.user_id]);

  return (
    <Popover
      open={open}
      onClose={handleClose}
      anchorEl={null}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ width: "450px", borderRadius: 10, marginTop: "48px" }}
    >
      <Box
        sx={{
          width: "500px",
          height: notifications.length > 0 ? "auto" : "85px",
          bgcolor: theme.palette.background.paper,
          p: 2,
          boxShadow: 1,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: theme.palette.primary.main }}
        >
          Notifications
        </Typography>
        {error ? (
          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
            {error}
          </Typography>
        ) : notifications.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            You have no new notifications at this time.
          </Typography>
        ) : (
          <List>
            {notifications.map((notification) => (
              <Notification key={notification.id} notification={notification} />
            ))}
          </List>
        )}
      </Box>
    </Popover>
  );
};

export default Notifications;
